import { useOrderStore } from "../store";
import { useCallback, useMemo } from "react";
import { useColumnOrderSizeColumns } from "http-hooks/szgrpdtl";

export const useOrderHeader = () => {
  const orderHeader = useOrderStore((state) => state.orderHeader);
  const setOrderHeader = useOrderStore((state) => state.setOrderHeader);
  const updateOrderHeader = useOrderStore((state) => state.updateOrderHeader);
  const resetOrderHeader = useOrderStore((state) => state.resetOrderHeader);
  const isHeaderComplete = useOrderStore((state) => state.isHeaderComplete);
  
  // Lot modal state and actions
  const isLotModalOpen = useOrderStore((state) => state.isLotModalOpen);
  const setLotModalOpen = useOrderStore((state) => state.setLotModalOpen);

  // Item detail modal state and actions
  const isItemDetailModalOpen = useOrderStore((state) => state.isItemDetailModalOpen);
  const setItemDetailModalOpen = useOrderStore((state) => state.setItemDetailModalOpen);
  const editingItemIndex = useOrderStore((state) => state.editingItemIndex);
  const setEditingItemIndex = useOrderStore((state) => state.setEditingItemIndex);

  // Protected cases state and actions
  const protectedCases = useOrderStore((state) => state.protectedCases);
  const setProtectedCases = useOrderStore((state) => state.setProtectedCases);

  // Lot data state and actions
  const lotData = useOrderStore((state) => state.lotData);
  const setLotData = useOrderStore((state) => state.setLotData);

  // Rate order items state and actions
  const rateOrderItems = useOrderStore((state) => state.rateOrderItems);
  const setRateOrderItems = useOrderStore((state) => state.setRateOrderItems);
  const populateRateOrderItemsFromOrder = useOrderStore((state) => state.populateRateOrderItemsFromOrder);

  // Column order items state and actions
  const columnOrderItems = useOrderStore((state) => state.columnOrderItems);
  const setColumnOrderItems = useOrderStore((state) => state.setColumnOrderItems);
  const populateColumnOrderItemsFromOrder = useOrderStore((state) => state.populateColumnOrderItemsFromOrder);

  // Select boxes state and actions
  const selectBoxes = useOrderStore((state) => state.selectBoxes);
  const updateSelectBox = useOrderStore((state) => state.updateSelectBox);
  const setSelectBoxes = useOrderStore((state) => state.setSelectBoxes);
  const resetSelectBoxes = useOrderStore((state) => state.resetSelectBoxes);

  // Pricelist filters state and actions
  const pricelistFilters = useOrderStore((state) => state.pricelistFilters);
  const setPricelistFilters = useOrderStore((state) => state.setPricelistFilters);

  // Fetch size columns from API for column order mapping
  const { data: sizeColumnsData } = useColumnOrderSizeColumns();
  
  // Size columns to be shown in the table
  const sizeColumns = useMemo(() => sizeColumnsData?.sizeColumns || [], [sizeColumnsData?.sizeColumns]);

  // Helper function to create consistent column ID mapping
  const createColumnId = useCallback((sz, szuom) => {
    return `${sz}_${szuom}`;
  }, []);

  // Create a mapping from size columns to their expected column IDs
  const sizeToColumnMap = useMemo(() => {
    const map = new Map();
    sizeColumns.forEach(column => {
      Object.entries(column.sizes || {}).forEach(([unit, sizeData]) => {
        if (sizeData?.itm_sz) {
          const columnId = createColumnId(sizeData.itm_sz, unit);
          // Map from size-based ID to column number/ID
          map.set(columnId, column.index);
        }
      });
    });
    
    return map;
  }, [sizeColumns, createColumnId]);

  return {
    orderHeader,
    setOrderHeader,
    updateOrderHeader,
    resetOrderHeader,
    isHeaderComplete,
    isLotModalOpen,
    setLotModalOpen,
    isItemDetailModalOpen,
    setItemDetailModalOpen,
    editingItemIndex,
    setEditingItemIndex,
    protectedCases,
    setProtectedCases,
    lotData,
    setLotData,
    rateOrderItems,
    setRateOrderItems,
    populateRateOrderItemsFromOrder,
    columnOrderItems,
    setColumnOrderItems,
    populateColumnOrderItemsFromOrder,
    selectBoxes,
    updateSelectBox,
    setSelectBoxes,
    resetSelectBoxes,
    pricelistFilters,
    setPricelistFilters,
    // Size column mapping for column orders
    sizeColumns,
    sizeToColumnMap,
    createColumnId,
  };
}; 